import { useState, useEffect } from 'react';
import { Users, UsersRound, TrendingUp, Monitor, ChevronDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

const layers = [
  {
    id: 'user',
    priority: 1,
    label: 'User Override',
    sublabel: 'Per-client special deals',
    who: 'Set by Broker',
    colorHex: '#ef4444',
    icon: Users,
    path: '/clients',
    pathLabel: 'Go to Clients',
    width: '100%',
    tooltip: 'Highest priority. Any field set here wins over everything else. Use for VIP clients, restricted accounts, or special deals.',
    fields: [
      { name: 'Max Leverage',       desc: 'Cap leverage for this specific client' },
      { name: 'Max Lot Size',       desc: 'Limit position size per trade' },
      { name: 'Margin Call Level',  desc: 'Override alert threshold' },
      { name: 'Stop Out Level',     desc: 'Override auto-close threshold' },
      { name: 'Max Open Positions', desc: 'Limit concurrent trades' },
      { name: 'Commission Per Lot', desc: 'Custom commission rate (e.g. VIP = 0)' },
      { name: 'Trading Disabled',   desc: 'Emergency kill switch for this client' },
    ],
    fallsTo: 'Account Group (P2)',
  },
  {
    id: 'group',
    priority: 2,
    label: 'Account Group',
    sublabel: 'VIP, Standard, Professional tiers',
    who: 'Set by Broker',
    colorHex: '#f97316',
    icon: UsersRound,
    path: '/account-groups',
    pathLabel: 'Go to Account Groups',
    width: '82%',
    tooltip: 'Applied to all clients in this group. If no user override exists, group settings apply. Great for tiered account types.',
    fields: [
      { name: 'Commission Per Lot', desc: 'Group-wide commission rate' },
      { name: 'Max Leverage',       desc: 'Group leverage cap' },
      { name: 'Margin Call Level',  desc: 'Group alert threshold' },
      { name: 'Stop Out Level',     desc: 'Group auto-close threshold' },
      { name: 'Max Open Positions', desc: 'Group position limit' },
      { name: 'Max Lot Size',       desc: 'Group trade size limit' },
    ],
    fallsTo: 'Symbol Settings (P3)',
  },
  {
    id: 'symbol',
    priority: 3,
    label: 'Symbol Settings',
    sublabel: 'Per-instrument broker config',
    who: 'Set by Broker',
    colorHex: '#3b82f6',
    icon: TrendingUp,
    path: '/instruments',
    pathLabel: 'Go to Instruments',
    width: '64%',
    tooltip: 'Controls pricing and execution rules per instrument. E.g. XAUUSD gets different spread markup than EURUSD.',
    fields: [
      { name: 'Spread Markup',      desc: 'Pips added to raw spread' },
      { name: 'Commission Per Lot', desc: 'Symbol-specific commission' },
      { name: 'Swap Markup',        desc: 'Added to overnight swap rates' },
      { name: 'Triple Swap Day',    desc: 'Day when 3x swap is charged' },
      { name: 'Max Leverage',       desc: 'Symbol leverage cap' },
      { name: 'Max Lot Size',       desc: 'Symbol trade size limit' },
      { name: 'Stop Level',         desc: 'Min SL/TP distance in pips' },
      { name: 'Freeze Level',       desc: 'Modification freeze distance' },
    ],
    fallsTo: 'Platform Defaults (P4)',
  },
  {
    id: 'platform',
    priority: 4,
    label: 'Platform Defaults',
    sublabel: 'System fallback & hard caps',
    who: 'Set by System Admin',
    colorHex: '#8b5cf6',
    icon: Monitor,
    path: null,
    pathLabel: null,
    width: '46%',
    tooltip: 'Lowest priority for defaults, but enforces absolute hard caps no other layer can override. Managed by system admin only.',
    fields: [
      { name: 'Default Leverage',    desc: 'Fallback if no layer sets it' },
      { name: 'Default Commission',  desc: 'Fallback commission rate' },
      { name: 'Default Margin Call', desc: 'Fallback alert level' },
      { name: 'Default Stop Out',    desc: 'Fallback auto-close level' },
      { name: 'Max Leverage Cap',    desc: 'Hard ceiling — no layer can exceed',  isCap: true },
      { name: 'Min Margin Call',     desc: 'Hard floor — no layer can go below',  isCap: true },
      { name: 'Min Stop Out',        desc: 'Hard floor — no layer can go below',  isCap: true },
      { name: 'Max Commission Cap',  desc: 'Hard ceiling on commission',          isCap: true },
      { name: 'Max Positions Cap',   desc: 'System-wide position limit',          isCap: true },
    ],
    fallsTo: null,
  },
];

// ─── Shared fallthrough notice ────────────────────────────────────────────────
function FallthroughNotice({ layer }) {
  return layer.fallsTo ? (
    <div style={{
      padding: '10px 12px',
      backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, var(--bg-tertiary))',
      border: '1px solid color-mix(in srgb, var(--accent-warning) 20%, var(--border-color))',
      borderRadius: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5',
    }}>
      💡 <strong style={{ color: 'var(--text-primary)' }}>Leave any field empty (NULL)</strong> to skip this layer
      and fall through to <strong style={{ color: 'var(--text-primary)' }}>{layer.fallsTo}</strong>.
    </div>
  ) : (
    <div style={{
      padding: '10px 12px',
      backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, var(--bg-tertiary))',
      border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, var(--border-color))',
      borderRadius: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5',
    }}>
      🛑 <strong style={{ color: 'var(--text-primary)' }}>Final fallback — no layer below.</strong>{' '}
      Fields marked <strong style={{ color: 'var(--accent-danger)' }}>CAP</strong> are hard limits enforced system-wide,
      no layer can bypass them.
    </div>
  );
}

// ─── Field list ───────────────────────────────────────────────────────────────
function FieldList({ layer, grid = false }) {
  return (
    <div style={{ display: grid ? 'grid' : 'block', gridTemplateColumns: grid ? '1fr 1fr' : undefined, gap: grid ? '6px' : undefined }}>
      {layer.fields.map((f, i) => (
        <div key={i}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px',
            padding: grid ? '8px 10px' : '7px 8px',
            borderRadius: '8px',
            backgroundColor: grid ? 'var(--bg-tertiary)' : 'transparent',
            border: grid ? `1px solid ${f.isCap ? 'color-mix(in srgb, var(--accent-danger) 20%, var(--border-color))' : 'var(--border-color)'}` : 'none',
            transition: grid ? 'none' : 'background 0.15s',
          }}
          onMouseEnter={e => { if (!grid) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
          onMouseLeave={e => { if (!grid) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div style={{
            width: '6px', height: '6px', borderRadius: '50%', marginTop: '4px', flexShrink: 0,
            backgroundColor: f.isCap ? '#ef4444' : layer.colorHex,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{f.name}</span>
              {f.isCap && (
                <span style={{
                  fontSize: '8px', fontWeight: '800', padding: '1px 5px', borderRadius: '4px',
                  backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)',
                  color: 'var(--accent-danger)',
                  border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)',
                  letterSpacing: '0.05em',
                }}>CAP</span>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px', lineHeight: '1.4' }}>{f.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Desktop layer card ───────────────────────────────────────────────────────
function DesktopLayerCard({ layer, isActive, onClick }) {
  const Icon = layer.icon;
  return (
    <div
      onClick={onClick}
      style={{
        width: layer.width, margin: '0 auto', cursor: 'pointer', borderRadius: '12px', padding: '13px 16px',
        border: `1px solid ${isActive ? `color-mix(in srgb, ${layer.colorHex} 35%, var(--border-color))` : 'var(--border-color)'}`,
        backgroundColor: isActive ? `color-mix(in srgb, ${layer.colorHex} 7%, var(--bg-secondary))` : 'var(--bg-secondary)',
        boxShadow: isActive ? `0 0 16px color-mix(in srgb, ${layer.colorHex} 12%, transparent)` : 'none',
        display: 'flex', alignItems: 'center', gap: '12px',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
        backgroundColor: isActive ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 12%, var(--bg-tertiary))`,
        color: isActive ? '#fff' : layer.colorHex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '800',
        border: `2px solid ${isActive ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 25%, transparent)`}`,
        transition: 'all 0.2s',
      }}>P{layer.priority}</div>

      <Icon style={{ width: '16px', height: '16px', flexShrink: 0, color: isActive ? layer.colorHex : 'var(--text-tertiary)', transition: 'color 0.2s' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)', transition: 'color 0.2s' }}>
            {layer.label}
          </span>
          <span style={{
            fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
            letterSpacing: '0.04em',
          }}>{layer.who.toUpperCase()}</span>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{layer.sublabel}</div>
      </div>

      <ChevronDown style={{
        width: '13px', height: '13px', flexShrink: 0,
        color: isActive ? layer.colorHex : 'var(--text-tertiary)',
        transform: isActive ? 'rotate(-90deg)' : 'rotate(0deg)',
        transition: 'all 0.2s',
      }} />
    </div>
  );
}

// ─── Detail panel (desktop right column) ─────────────────────────────────────
function DetailPanel({ layer, navigate }) {
  const Icon = layer.icon;
  return (
    <div style={{
      border: `1px solid color-mix(in srgb, ${layer.colorHex} 25%, var(--border-color))`,
      borderRadius: '14px', overflow: 'hidden',
      backgroundColor: 'var(--bg-secondary)',
      boxShadow: `0 0 24px color-mix(in srgb, ${layer.colorHex} 8%, transparent)`,
      animation: 'panelIn 0.18s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        borderBottom: `1px solid color-mix(in srgb, ${layer.colorHex} 15%, var(--border-color))`,
        backgroundColor: `color-mix(in srgb, ${layer.colorHex} 5%, var(--bg-secondary))`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Icon style={{ width: '17px', height: '17px', color: layer.colorHex }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{layer.label}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Priority {layer.priority} · {layer.who}</div>
          </div>
        </div>
        {layer.path && (
          <button onClick={() => navigate(layer.path)} style={{
            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600',
            padding: '5px 9px', borderRadius: '8px', cursor: 'pointer',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
          }}>
            <ExternalLink style={{ width: '11px', height: '11px' }} />{layer.pathLabel}
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <FallthroughNotice layer={layer} />
        <FieldList layer={layer} />
      </div>
    </div>
  );
}

// ─── Mobile accordion card ────────────────────────────────────────────────────
function MobileLayerCard({ layer, navigate }) {
  const [open, setOpen] = useState(layer.priority === 1);
  const Icon = layer.icon;

  return (
    <div style={{
      borderRadius: '14px', overflow: 'hidden',
      border: `1px solid ${open ? `color-mix(in srgb, ${layer.colorHex} 30%, var(--border-color))` : 'var(--border-color)'}`,
      backgroundColor: 'var(--bg-secondary)',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxShadow: open ? `0 0 14px color-mix(in srgb, ${layer.colorHex} 8%, transparent)` : 'none',
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '11px',
        padding: '13px 15px', textAlign: 'left',
        backgroundColor: open ? `color-mix(in srgb, ${layer.colorHex} 5%, var(--bg-secondary))` : 'transparent',
        borderBottom: open ? `1px solid color-mix(in srgb, ${layer.colorHex} 12%, var(--border-color))` : 'none',
      }}>
        <div style={{
          width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
          backgroundColor: open ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 12%, var(--bg-tertiary))`,
          color: open ? '#fff' : layer.colorHex,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: '800',
          border: `2px solid ${open ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 25%, transparent)`}`,
        }}>P{layer.priority}</div>
        <Icon style={{ width: '15px', height: '15px', color: open ? layer.colorHex : 'var(--text-tertiary)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{layer.label}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{layer.sublabel}</div>
        </div>
        <ChevronDown style={{
          width: '15px', height: '15px', flexShrink: 0, color: 'var(--text-tertiary)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s',
        }} />
      </button>

      {open && (
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', fontSize: '10px', fontWeight: '700',
            padding: '3px 8px', borderRadius: '20px',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
          }}>{layer.who.toUpperCase()}</div>

          <FallthroughNotice layer={layer} />
          <FieldList layer={layer} grid />

          {layer.path && (
            <button onClick={() => navigate(layer.path)} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
              color: layer.colorHex,
              border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
            }}>
              <ExternalLink style={{ width: '13px', height: '13px' }} />{layer.pathLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function CascadePage() {
  const [activeId, setActiveId] = useState('user');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const activeLayer = layers.find(l => l.id === activeId);

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh' }} className="-mx-4 -my-6">
        <style>{`@keyframes panelIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* Hero */}
        <div style={{
          padding: '20px 16px 24px',
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)',
        }}>
          <p style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
            SETTINGS ARCHITECTURE
          </p>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            Cascade Priority
          </h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.5', margin: 0 }}>
            Settings resolve highest → lowest. Leave a field{' '}
            <strong style={{ color: '#fbbf24' }}>empty (NULL)</strong>{' '}
            to fall through to the next layer.
          </p>
        </div>

        {/* Flow pills */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 14px',
          overflowX: 'auto', borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          {layers.map((l, i) => (
            <>
              <span key={l.id} style={{
                fontSize: '10px', fontWeight: '800', padding: '3px 7px', borderRadius: '20px', flexShrink: 0,
                backgroundColor: `color-mix(in srgb, ${l.colorHex} 10%, var(--bg-tertiary))`,
                color: l.colorHex,
                border: `1px solid color-mix(in srgb, ${l.colorHex} 20%, var(--border-color))`,
              }}>P{l.priority} {l.label}</span>
              {i < layers.length - 1 && (
                <span key={`a${i}`} style={{ fontSize: '11px', color: 'var(--text-tertiary)', flexShrink: 0 }}>→</span>
              )}
            </>
          ))}
        </div>

        {/* Accordion */}
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {layers.map((layer, idx) => (
            <div key={layer.id}>
              <MobileLayerCard layer={layer} navigate={navigate} />
              {idx < layers.length - 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 8px' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    ↓ if empty, falls through
                  </span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <style>{`@keyframes panelIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold mb-2">Cascade Priority System</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Settings resolve highest → lowest priority. Leave a field{' '}
          <strong style={{ color: 'var(--accent-warning)' }}>empty (NULL)</strong>{' '}
          to skip that layer and fall through to the next.
        </p>
      </div>

      {/* Resolution flow bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        padding: '11px 14px', borderRadius: '12px',
        backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
      }}>
        <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.08em', marginRight: '2px' }}>
          RESOLUTION ORDER:
        </span>
        {layers.map((l, i) => (
          <>
            <button key={l.id} onClick={() => setActiveId(l.id)} style={{
              fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
              backgroundColor: activeId === l.id ? l.colorHex : `color-mix(in srgb, ${l.colorHex} 10%, var(--bg-tertiary))`,
              color: activeId === l.id ? '#fff' : l.colorHex,
              border: `1px solid color-mix(in srgb, ${l.colorHex} 25%, var(--border-color))`,
              transition: 'all 0.15s ease',
            }}>P{l.priority} · {l.label}</button>
            {i < layers.length - 1 && (
              <span key={`a${i}`} style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>→</span>
            )}
          </>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-tertiary)' }}>
          First non-null value wins
        </span>
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

        {/* Left: pyramid */}
        <div>
          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.08em', marginBottom: '10px' }}>
            HIGHEST PRIORITY — CLICK ANY LAYER TO EXPLORE
          </p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {layers.map((layer, idx) => (
              <div key={layer.id}>
                <DesktopLayerCard layer={layer} isActive={activeId === layer.id} onClick={() => setActiveId(layer.id)} />
                {idx < layers.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      ↓ if empty, falls through
                    </span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {layers.map(l => (
              <div key={l.id} style={{
                padding: '10px 12px', borderRadius: '10px',
                backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: l.colorHex, flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>{l.label}</span>
                </div>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0, lineHeight: '1.4' }}>{l.tooltip}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: detail panel */}
        <div style={{ position: 'sticky', top: '24px' }}>
          {activeLayer && <DetailPanel layer={activeLayer} navigate={navigate} />}
        </div>
      </div>
    </div>
  );
}